import { get } from 'lodash';
import { CircleOff } from 'lucide-react';

import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { VEHICLE } from '@/lib/constants';
import { colors } from '@/theme/constants';
import { LiveTracking, MarkerSize, PositionInterface } from '@/types';
import { NodeType, PlaceType } from '@/types/api-types';
import { calculateBearing } from '@/utils/mapping/geometry';
import { tv } from '@/utils/styles';

export interface TrackingMarkerProps {
  size?: MarkerSize;
  zIndex?: string | number;
  color: string;
  label: string;
  hasStaticLabel?: boolean;
  type: NodeType | PlaceType;
  iconName: string;
  isActive: boolean;
  location: PositionInterface;
  previousLocation?: PositionInterface;
  bearing?: number;
  liveTracking?: LiveTracking;
  isHidden?: boolean;
}

const computeHeading = (
  sourceLocation?: PositionInterface,
  targetLocation?: PositionInterface,
  explicitBearing?: number,
) => {
  if (typeof explicitBearing === 'number') {
    return explicitBearing;
  }

  if (!sourceLocation || !targetLocation) return 0;

  return calculateBearing(sourceLocation, targetLocation);
};

const TrackingMarker = ({
  size = 'sm',
  color,
  label,
  hasStaticLabel,
  type,
  iconName,
  isActive,
  zIndex,
  location,
  previousLocation,
  bearing,
  liveTracking,
  isHidden = false,
}: TrackingMarkerProps) => {
  if (isHidden) return null;

  const isVehicle = type === VEHICLE;
  const vehicleHeading = isVehicle ? computeHeading(previousLocation, location, bearing) : 0;
  const backgroundColor = color && get(colors, color, color);
  const borderColor = color && get(colors, color, color);

  const { base, liveMarker, markerLabel, liveTrackingLabel, liveTrackingArrow, liveTrackingSubtext } = styles({
    size,
    isActive,
  });

  return (
    <Tooltip shouldUsePortal content={label} isOpen={false} disabled={!label}>
      <div className={base()} style={{ zIndex, backgroundColor, borderColor }}>
        {hasStaticLabel && (
          <div className={markerLabel()}>
            <span className="ml-0.5">{label}</span>
          </div>
        )}
        {liveTracking?.isSupported && (
          <div className={liveTrackingLabel()}>
            <span className={liveTrackingArrow()} />
            <div>Estimated vessel location</div>
            {!liveTracking.isActive && (
              <div className={liveTrackingSubtext()}>
                <CircleOff className="size-2.5" /> Live tracking unavailable
              </div>
            )}
          </div>
        )}
        <div className={liveMarker()} style={{ transform: `rotate(${vehicleHeading}deg)` }}>
          {iconName && <Icon name={iconName} />}
        </div>
      </div>
    </Tooltip>
  );
};

const styles = tv({
  slots: {
    base: 'border-1 relative z-4 flex content-center items-center justify-center rounded-full text-white hover:z-20 [&_svg]:block [&_svg]:h-full [&_svg]:w-full',
    liveMarker: 'grid size-6 place-items-center rounded-full transition-all [&_*]:block [&_svg]:!size-4',
    markerLabel:
      'absolute bottom-0 left-0 top-0 z-1 flex items-center whitespace-nowrap rounded-xl bg-white px-1.5 pl-5 font-medium text-text-primary shadow',
    liveTrackingLabel:
      'absolute left-9 top-0 z-1 whitespace-nowrap rounded-md bg-blue-700 px-2.5 py-2 text-sm text-blue-700 text-white',
    liveTrackingSubtext: 'mt-1 flex items-center gap-1.5 text-xs font-normal italic text-white/70',
    liveTrackingArrow:
      'absolute -left-[5px] top-3.5 z-1 h-0 w-0 -translate-y-1/2 rotate-[315deg] border-b-0 border-l-0 border-r-[10px] border-t-[10px] border-solid border-b-transparent border-l-transparent border-r-transparent border-t-blue-700',
  },
  variants: {
    size: {
      xs: 'size-1',
      sm: '-ml-[3px] -mt-[3px] size-1.5 border-2 !bg-[#bee0ee]',
      md: '-ml-1.5 -mt-1.5 size-3',
      lg: '-ml-2 -mt-2 size-4',
      xl: '-ml-2 -mt-2 size-5',
      '2xl': '-ml-3 -mt-3 size-6',
      '3xl': '-ml-5 -mt-5 size-10 [&_svg]:!size-5',
    },
    isActive: {
      true: {
        base: 'border-none',
        liveMarker: 'animate-light-blue-pulse bg-lightBlue-500/20',
      },
      false: '',
    },
  },
});

export { TrackingMarker };
